import React, { useEffect, useState, useCallback } from "react";
import {Tooltip} from "@mui/material";
import { MessageDialog } from "../components/MessageDialog";
import DuplicateAlbumModal from "./DuplicateAlbumModal";
import CreateCoverAlbumModal from "./CreateCoverAlbumModal";

interface AlbumSize {
    width: number;
    height: number;
    unit: string;
}

export interface Album {
    _id: string;
    albumName: string;
    albumType?: string;
    pageCount: number;
    albumSize: AlbumSize;
    createdAt: string;
    printExported?: boolean; //  add this
    webProofExported?: boolean; //  NEW
}

interface AlbumListPanelProps {
    token: string;
    onOpenProject: (album: Album) => void;
    refreshTrigger?: number;
    onAlbumSaved?: () => void; // Added callback
}

export const COVER_TYPE_BY_PRODUCT = {
    cover: "AC",
    scase: "ASC",
} as const;

export default function AlbumListPanel({
    token,
    onOpenProject,
    refreshTrigger = 0,
    onAlbumSaved,
}: AlbumListPanelProps) {
    const [albums, setAlbums] = useState<Album[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [albumToDelete, setAlbumToDelete] = useState<Album | null>(null);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [albumToDuplicate, setAlbumToDuplicate] = useState<Album | null>(null);
    const [coverOpen, setCoverOpen] = useState(false);
    const [albumForCover, setAlbumForCover] = useState<Album | null>(null);
    const BASE_PATH = process.env.PUBLIC_URL || "";
    const API_URL = process.env.REACT_APP_API_URL;

    
    const fetchAlbums = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/albums`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch albums");
            const data = await res.json();
            const normalizedAlbums = data.map((a: Album) => ({
                ...a,
                albumSize: a.albumSize ?? { width: 0, height: 0, unit: "cm" },
            }));
            setAlbums(normalizedAlbums);
        
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAlbums();
    }, [fetchAlbums, refreshTrigger]);

    async function handleOpenProject(id: string) {
        try {
            const res = await fetch(`${API_URL}/albums/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to load album");
            const albumData = await res.json();
            onOpenProject(albumData);
        } catch (err) {
            alert("Error opening album: " + (err as Error).message);
        }
    }

    function requestDelete(album: Album) {
        setAlbumToDelete(album);
        setConfirmOpen(true);
    }

    function confirmDelete() {
        if (!albumToDelete) return;

        fetch(`${API_URL}/albums/${albumToDelete._id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                if (!res.ok) throw new Error("Failed to delete album");
                onAlbumSaved?.(); // refresh parent
            })
            .catch((err) => alert("Error deleting album: " + err.message))
            .finally(() => {
                setConfirmOpen(false);
                setAlbumToDelete(null);
            });
    }

    function handleDuplicate(album: Album) {
        setAlbumToDuplicate(album);
        setDuplicateOpen(true);
    }

    function handleCreateCover(album: Album) {
        setAlbumForCover(album);
        setCoverOpen(true);
    }


    function handleDuplicated() {
        fetchAlbums(); // refresh list after duplication
    }


    if (loading) return <p>Loading albums...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;

    const CANVAS_WRAP = 4.723;
    function formatDisplaySize(size: number): string {
        return parseFloat(size.toFixed(3)).toString();
    }

    const NON_FOLDED_PRODUCTS = ["MONTAGE", "CANVAS", "FCANVAS", "ARCH", "PRINTS", "FMOUNTS", "HIPSTER", "HANDTORN", "FPRINTS", "AC", "ASC" ] as string[];

    const disable_products = ["MONTAGE", "CANVAS", "FCANVAS", "ARCH", "PRINTS", "FPRINTS", "FMOUNTS", "HIPSTER", "HANDTORN", "LUXE", "AC", "ASC"] as string[];
    const COVER_CAPABLE_PRODUCTS = [
        "BOUTIQUE",
        "LIFESTYLE",
        "GUEST",
    ] as const;

    const SLIPCASE_CAPABLE_PRODUCTS = [
        "LIFESTYLE", // ONLY this one
    ] as const;



    return (
        <>
            <div
                className="albums-table"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "70vh",
                }}
            >

            <div className="table-header">
                <div className="header-cell">Album Name</div>
                <div className="header-cell">Type</div>
                <div className="header-cell">Size</div>
                <div className="header-cell">Spreads</div>
                <div className="header-cell">Created</div>
                <div className="header-cell">Actions</div>
            </div>

                <div
                    className="albums-list"
                    style={{
                        overflowY: "auto",
                        flex: 1,
                    }}
                >
                    {[...albums]
                        .sort(
                            (a, b) =>
                                new Date(b.createdAt).getTime() -
                                new Date(a.createdAt).getTime()
                        )
                        .map((album) => {
                        const albumType = album.albumType || "";
                        //  DUPLICATE RULE (keep your old logic)
                        const isDuplicateDisabled = disable_products.includes(albumType);

                        //  COVER / SLIPCASE RULE (new logic)
                        const canHaveCover = COVER_CAPABLE_PRODUCTS.includes(albumType as any);
                        const canHaveSlipcase = SLIPCASE_CAPABLE_PRODUCTS.includes(albumType as any);
                        const isCoverDisabled = !canHaveCover && !canHaveSlipcase;

                        const coverLabel =
                            canHaveCover && canHaveSlipcase
                                ? "Create Cover or Slipcase"
                                : canHaveCover
                                    ? "Create Album Cover"
                                    : canHaveSlipcase
                                        ? "Create Slipcase"
                                        : "Not available for this product";

                        return (
                    <div key={album._id} className="table-row">
                        <div className="table-cell album-name">{album.albumName}</div>
                        <div className="table-cell album-name">{album.albumType}</div>                     
                        <div className="table-cell album-size">
                            {album.albumType === "CANVAS" || album.albumType === "FCANVAS"
                                ? `${formatDisplaySize(album.albumSize.width - CANVAS_WRAP)} x ${formatDisplaySize(album.albumSize.height - CANVAS_WRAP)} ${"in"}`
                                : NON_FOLDED_PRODUCTS.includes(String(album.albumType))
                                    ? `${formatDisplaySize(album.albumSize.width)} x ${formatDisplaySize(album.albumSize.height)} ${album.albumSize.unit}`
                                    : `${formatDisplaySize(album.albumSize.width / 2)} x ${formatDisplaySize(album.albumSize.height)} ${album.albumSize.unit}`
                            }
                        </div>
                        <div className="table-cell album-pages">{album.pageCount}</div>
                        <div className="table-cell album-exported">
                            {new Date(album.createdAt).toLocaleDateString()}
                        </div>                    
                        <div className="table-cell album-actions">
                                <Tooltip
                                        title={
                                            album.printExported
                                                ? "The album has been sent to print"
                                                : album.webProofExported
                                                    ? "The album is in review (web proof sent)"
                                                    : "Open Album"
                                        }
                                    arrow
                                >
                                    <button
                                        className="icon-btn"
                                        onClick={() => handleOpenProject(album._id)}
                                    >
                                        <img
                                            src={
                                                album.printExported
                                                        ? `${BASE_PATH}/open_album_exp.png`
                                                        : album.webProofExported
                                                            ? `${BASE_PATH}/open_album_web.png`
                                                            : `${BASE_PATH}/open_album.png`
                                                    
                                            }
                                            alt="Open"
                                        />
                                    </button>

                                </Tooltip>

                                    <Tooltip
                                        title={isDuplicateDisabled ? "Not available for this product" : "Duplicate Album"}
                                        arrow
                                    >
                                        <span>
                                            <button
                                                className="icon-btn"
                                                onClick={() => handleDuplicate(album)}
                                                disabled={isDuplicateDisabled}
                                                style={{
                                                    opacity: isDuplicateDisabled ? 0.4 : 1,
                                                    cursor: isDuplicateDisabled ? "not-allowed" : "pointer",
                                                }}
                                            >
                                                <img src={`${BASE_PATH}/duplicate_album.png`} alt="Duplicate" />
                                            </button>
                                        </span>
                                    </Tooltip>

           
                            {/* Create Cover Album button (disabled for cover or case albums) */}
                                    <Tooltip title={coverLabel} arrow>
                                        <span>
                                            <button
                                                className="icon-btn"
                                                onClick={() => handleCreateCover(album)}
                                                disabled={isCoverDisabled}
                                                style={{
                                                    opacity: isCoverDisabled ? 0.4 : 1,
                                                    cursor: isCoverDisabled ? "not-allowed" : "pointer",
                                                }}
                                            >
                                                <img src={`${BASE_PATH}/album_cover.png`} alt="Create Cover" />
                                            </button>
                                        </span>
                                    </Tooltip>

                            <Tooltip title="Delete Album" arrow>
                                <button
                                    className="icon-btn"
                                    onClick={() => requestDelete(album)}
                                >
                                        <img src={`${process.env.PUBLIC_URL}/delete_album.png`} alt="Delete" />
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                        );
                    })}
            </div>
        </div>
            {/* Custom Windows-style delete confirmation dialog */}
            <MessageDialog
                open={confirmOpen}
                title={albumToDelete?.albumName}
                message="Are you sure you want to delete this album?"
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmDelete}
                confirmText="Delete"
                confirmColor="#b71c1c"
                cancelColor="#e0e0e0"
            />
            {/* Duplicate album modal */}
            <DuplicateAlbumModal
                open={duplicateOpen}
                onClose={() => setDuplicateOpen(false)}
                originalAlbum={albumToDuplicate}
                token={token}
                onDuplicated={handleDuplicated}
            />
            <CreateCoverAlbumModal
                open={coverOpen}
                onClose={() => setCoverOpen(false)}
                album={albumForCover}
                token={token}
                defaultType="scase"   //  ADD THIS
                onCreated={(newAlbum) => {
                    setCoverOpen(false);
                    fetchAlbums();
                    handleOpenProject(newAlbum._id);
                }}
            />
        </>
    );
}
